// Saved-views dropdown for the dashboard header.
//
// A "view" is a snapshot of the CURRENT filter/group state (see
// dashboard.tsx's serializeFiltersToSearchParams) — not the browser URL,
// which normal chip-toggling deliberately never touches.
//
// Stored per-user in team_members.saved_dashboard_views jsonb (migration 033).
// The mutation hooks are in queries.ts.
//
// UX: trigger button shows a count badge. Open list shows each view as a
// row with a delete (×) on hover. "Save current view…" opens an in-app
// dialog to name it (NOT window.prompt/window.confirm — browsers silently
// suppress repeated native dialogs on a page after a few uses, which made
// Save look like it was doing nothing with zero visible error. An in-app
// Dialog can't be blocked that way, and it's consistent with every other
// confirm/name-entry flow elsewhere in this app).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { toastFailure } from '@/lib/toast';
import { Bookmark, ChevronDown, Plus, X, XCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useDeleteDashboardView,
  useMyDashboardViews,
  useSaveDashboardView,
  type SavedDashboardView,
} from '@/lib/queries';
import { useCurrentInbox } from '@/lib/inbox-scope';
import { cn } from '@/lib/utils';

interface SavedDashboardViewsProps {
  /** Query-string serialization of the CURRENTLY APPLIED filters/group —
   *  from dashboard.tsx's serializeFiltersToSearchParams(filters, group).
   *  NOT location.search: normal chip-toggling deliberately never touches
   *  the address bar, so the URL alone can't tell us what's actually
   *  filtered right now (that was the root cause of saved views silently
   *  capturing nothing). */
  currentSearch: string;
  /** Resets filters + group back to the default (no view applied). */
  onClear: () => void;
}

export function SavedDashboardViews({ currentSearch, onClear }: SavedDashboardViewsProps) {
  const navigate = useNavigate();
  // Filter presets (priority/status/category/assignee/date-range) are
  // inbox-agnostic — "My P1s" means the same thing in any inbox. Apply
  // always stays in whatever inbox the admin is currently looking at.
  const { currentInbox } = useCurrentInbox();
  const viewsQ = useMyDashboardViews();
  const saveView = useSaveDashboardView();
  const deleteView = useDeleteDashboardView();
  const [open, setOpen] = useState(false);

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [pendingDelete, setPendingDelete] = useState<SavedDashboardView | null>(null);

  const views = viewsQ.data ?? [];
  // Highlight the active view by exact-string match. Two views with the
  // same filter set but different names will both highlight, which is fine.
  const activeId = views.find((v) => v.search === currentSearch)?.id ?? null;
  const hasActiveFilters = currentSearch.length > 0;

  function openSaveDialog() {
    setOpen(false);
    setSaveName('');
    setSaveOpen(true);
  }

  function handleSaveConfirm() {
    const name = saveName.trim();
    if (!name) return;
    saveView.mutate(
      { name, search: currentSearch },
      {
        onSuccess: () => {
          toast.success(`View "${name}" saved`);
          setSaveOpen(false);
        },
        onError: (err) => toastFailure(err, "Couldn't save the view"),
      },
    );
  }

  function handleApply(view: SavedDashboardView) {
    // Navigate WITHIN the current inbox scope directly — the legacy
    // unscoped `/dashboard` path bounces through DefaultInboxRedirect's
    // "last visited inbox" heuristic, which can silently land the admin on
    // a different inbox than the one they were viewing when they clicked
    // the saved view (previously the actual cause of "views don't work").
    const base = `/inbox/${currentInbox.label_namespace}/dashboard`;
    navigate(view.search ? `${base}?${view.search}` : base);
    setOpen(false);
  }

  function handleClear() {
    onClear();
    setOpen(false);
  }

  function requestDelete(view: SavedDashboardView, e: React.MouseEvent) {
    e.stopPropagation();
    setPendingDelete(view);
  }

  function handleDeleteConfirm() {
    if (!pendingDelete) return;
    const view = pendingDelete;
    deleteView.mutate(view.id, {
      onSuccess: () => {
        toast.success('View deleted');
        setPendingDelete(null);
      },
      onError: (err) => toastFailure(err, "Couldn't delete the view"),
    });
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-2xs font-medium transition-colors',
              activeId
                ? 'border-transparent bg-foreground text-background'
                : 'border-border/70 bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            <Bookmark className="h-3 w-3" />
            {activeId ? views.find((v) => v.id === activeId)?.name ?? 'Views' : 'Views'}
            {views.length > 0 ? (
              <span
                className={cn(
                  'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-3xs font-semibold',
                  activeId ? 'bg-background/20 text-background' : 'bg-muted text-muted-foreground',
                )}
              >
                {views.length}
              </span>
            ) : null}
            <ChevronDown className="h-3 w-3 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 p-1">
          {views.length === 0 ? (
            <p className="px-3 py-3 text-xs text-muted-foreground">
              No saved views yet. Filter the dashboard, then click "Save current view".
            </p>
          ) : (
            <ul>
              {views.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => handleApply(v)}
                    className={cn(
                      'group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-accent/60',
                      v.id === activeId && 'bg-accent text-foreground',
                    )}
                  >
                    <Bookmark className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{v.name}</span>
                    <button
                      type="button"
                      onClick={(e) => requestDelete(v, e)}
                      className="hidden shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive group-hover:inline-flex"
                      aria-label={`Delete ${v.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="my-1 h-px bg-border/60" />
          {hasActiveFilters && (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                handleClear();
              }}
              className="gap-2 text-xs"
            >
              <XCircle className="h-3 w-3" />
              Clear filters
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              openSaveDialog();
            }}
            className="gap-2 text-xs"
          >
            <Plus className="h-3 w-3" />
            Save current view…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save current view</DialogTitle>
            <DialogDescription>
              Snapshots the filters and tab you have selected right now, so you can jump back to
              this exact combination later.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveConfirm();
            }}
            className="space-y-2"
          >
            <Label htmlFor="saved-view-name">Name</Label>
            <Input
              id="saved-view-name"
              autoFocus
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder='e.g. "My P1s", "Stale waiting on client"'
            />
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} disabled={saveView.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfirm} disabled={!saveName.trim() || saveView.isPending}>
              {saveView.isPending ? 'Saving…' : 'Save view'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete view</DialogTitle>
            <DialogDescription>
              {pendingDelete
                ? `Delete "${pendingDelete.name}"? This can't be undone.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)} disabled={deleteView.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleteView.isPending}>
              {deleteView.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
